"use client";
import { useRef, useState } from "react";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { Container } from "@/components";

interface TeamOffTheClockProps {
  name: string;
  quote: string;
  funFacts: string[];
}

const TeamOffTheClock = ({ name, quote, funFacts }: TeamOffTheClockProps) => {
  const first = name.split(" ")[0];
  const sectionRef = useRef<HTMLElement | null>(null);
  const [revealed, setRevealed] = useState(false);
  const [reduced] = useState(
    () =>
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );

  // Arm the scene: hide the text + droplet + rings so the pool starts empty.
  // Under reduced motion the text stays visible and only the droplet is hidden.
  const { contextSafe } = useGSAP(
    () => {
      gsap.set(".wr-ring", { xPercent: -50, yPercent: -50, opacity: 0 });
      gsap.set(".wr-droplet", { xPercent: -50, yPercent: -50, opacity: 0 });
      if (reduced) return;
      gsap.set(".wr-line", { opacity: 0, y: 22, filter: "blur(8px)" });
    },
    { scope: sectionRef, dependencies: [reduced] },
  );

  const play = contextSafe(() => {
    const IMPACT = 0.5;
    const disp = "#teamRipple feDisplacementMap";
    const tl = gsap.timeline({ onStart: () => setRevealed(true) });

    // Reset to the armed state so "Drop again" replays cleanly.
    tl.set(".wr-line", { opacity: 0, y: 22, filter: "blur(8px)" });
    tl.set(".wr-ring", { scale: 0.2, opacity: 0 });
    tl.set(disp, { attr: { scale: 0 } });
    tl.set(".wr-droplet", { opacity: 1, y: -180, scaleX: 0.7, scaleY: 1.7 }, 0);

    // Droplet accelerates down and hits the surface.
    tl.to(".wr-droplet", { y: 0, duration: 0.5, ease: "power2.in" }, 0);
    tl.to(
      ".wr-droplet",
      { scaleX: 1.9, scaleY: 0.35, duration: 0.12, ease: "power2.out" },
      IMPACT,
    );
    tl.to(".wr-droplet", { opacity: 0, duration: 0.25 }, IMPACT + 0.06);

    // Concentric rings ripple outward.
    tl.fromTo(
      ".wr-ring",
      { scale: 0.2, opacity: 0.55 },
      {
        scale: 14,
        opacity: 0,
        duration: 1.2,
        ease: "power2.out",
        stagger: 0.12,
      },
      IMPACT,
    );

    // Water disturbance: kick the displacement then settle it to crisp.
    tl.set(disp, { attr: { scale: 38 } }, IMPACT);
    tl.to(
      disp,
      { attr: { scale: 0 }, duration: 1.3, ease: "power2.out" },
      IMPACT + 0.02,
    );

    // Text surfaces through the settling water.
    tl.to(
      ".wr-line",
      {
        opacity: 1,
        y: 0,
        filter: "blur(0px)",
        duration: 0.85,
        ease: "power3.out",
        stagger: 0.12,
      },
      IMPACT + 0.14,
    );
  });

  return (
    <section
      ref={sectionRef}
      className="rounded-b-4xl bg-card-foreground py-16 md:py-24"
    >
      {/* Inline filter def — the "magic water" distortion applied to the text. */}
      <svg aria-hidden width="0" height="0" className="absolute">
        <filter id="teamRipple">
          <feTurbulence
            type="turbulence"
            baseFrequency="0.018 0.03"
            numOctaves={2}
            seed={7}
            result="turb"
          />
          <feDisplacementMap
            in="SourceGraphic"
            in2="turb"
            scale={0}
            xChannelSelector="R"
            yChannelSelector="G"
          />
        </filter>
      </svg>

      <Container className="px-4 lg:px-10">
        <div className="space-y-3 text-center">
          <p className="text-sm font-semibold uppercase tracking-[0.25em] text-primary">
            Off The Clock
          </p>
          <h2 className="font-black uppercase tracking-tighter text-3xl text-white md:text-5xl">
            {first.toUpperCase()}, UNPLUGGED
          </h2>
        </div>

        {/* The pool */}
        <div
          className="relative mx-auto mt-14 grid min-h-100 max-w-3xl place-items-center overflow-hidden rounded-[2.5rem] border border-white/10 px-6 py-16 md:mt-16 md:min-h-130"
          style={{
            background:
              "radial-gradient(70% 60% at 50% 55%, rgba(178,209,39,0.08), transparent 70%)",
          }}
        >
          {/* Surface line at the impact point */}
          <div
            aria-hidden
            className="pointer-events-none absolute left-0 top-1/2 h-px w-full -translate-y-1/2 bg-linear-to-r from-transparent via-primary/25 to-transparent"
          />

          {/* Ripple rings (centered on the surface) */}
          {[0, 1, 2].map((r) => (
            <span
              key={r}
              aria-hidden
              className="wr-ring pointer-events-none absolute left-1/2 top-1/2 h-16 w-16 rounded-full border border-primary/70"
            />
          ))}

          {/* Falling droplet */}
          <span
            aria-hidden
            className="wr-droplet pointer-events-none absolute left-1/2 top-1/2 h-3.5 w-3.5 rounded-full bg-primary"
            style={{ boxShadow: "0 0 14px 2px rgba(178,209,39,0.7)" }}
          />

          {/* The revealed content — distorted by the ripple filter as it surfaces */}
          <div
            className="relative z-10 flex flex-col items-center gap-8 text-center"
            style={{ filter: "url(#teamRipple)" }}
          >
            <blockquote className="wr-line max-w-xl text-2xl font-bold leading-snug text-white md:text-3xl">
              &ldquo;{quote}&rdquo;
            </blockquote>

            <ul className="flex flex-col items-center gap-3">
              {funFacts.map((fact) => (
                <li
                  key={fact}
                  className="wr-line flex items-center gap-2.5 text-sm text-white/70 md:text-base"
                >
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                  {fact}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Trigger */}
        {!reduced && (
          <div className="mt-10 flex flex-col items-center gap-3">
            <button
              type="button"
              onClick={play}
              className="group inline-flex cursor-pointer items-center gap-3 rounded-full border border-primary/40 bg-primary/10 px-7 py-3 text-sm font-bold uppercase tracking-[0.15em] text-primary transition-all duration-300 hover:bg-primary hover:text-black active:scale-95"
            >
              <span
                className="h-2.5 w-2.5 rounded-full bg-primary transition-transform duration-300 group-hover:translate-y-0.5 group-hover:bg-black"
                aria-hidden
              />
              {revealed ? "Drop again" : "Tap the water"}
            </button>
            {!revealed && (
              <p className="text-xs uppercase tracking-[0.2em] text-white/40">
                Something surfaces
              </p>
            )}
          </div>
        )}
      </Container>
    </section>
  );
};

export default TeamOffTheClock;
